import React, { PureComponent } from "react"
import { mergeFunctions } from "./helpers"
import CoreLoading from "./loading"
import PackageLoader from "./PackageLoader"
import { loadPackage } from "./loadPackage"

const DEBUG = import.meta.env.REACT_APP_DEBUG === "true"

const APP_VERSION = import.meta.env.REACT_APP_VERSION

// This the Core of the application which is responsible for:
// Load packages; Invoke the Managers; Store imported Components

class Core extends PureComponent {
  // It is an Object where the key is the name of the component and the value
  // is an Array of two positions. The 1st is the component wrapped by
  // all Managers and the 2nd is the config object.
  Components = {}
  // Stores Wrapped React Components, this is the "Components" prop
  _components = {}
  // It is needed to store the original imported component because when we
  // need to compose again (a new package arrived), we need to start fresh.
  // Avoids Composer fn running more than oen time for same component instance.
  originalComponents = {}
  // Array of imported Managers
  managers = []
  // List of Strings of imported packages.
  // Useful to avoid fetching the same package twice
  packagesLoaded = []

  newerVersion = (oldVersion, newVersion) => {
    const [major1, minor1, patch1] = oldVersion.split(".").map((n) => parseInt(n))
    const [major2, minor2, patch2] = newVersion.split(".").map((n) => parseInt(n))
    return major1 < major2 || minor1 < minor2 || patch1 < patch2
  }

  // If the APP_VERSION, or `meta.json` version, is bumped, clean the localStorage.
  verifyVersion = async (currentVersion) => {
    try {
      if (currentVersion === null) currentVersion = "0.0.0"

      let newVersion = currentVersion
      const metaVersion = await fetch(`meta.json?t=${new Date().getTime()}`, {})
        .then((a) => a.json())
        .then((a) => a.version)
        .catch((err) => {
          console.error("Error fetching `meta.json` while trying to verify version.", err)
          return "0.0.0"
        })

      if (this.newerVersion(newVersion, APP_VERSION)) newVersion = APP_VERSION
      if (this.newerVersion(newVersion, metaVersion)) newVersion = metaVersion

      if (newVersion !== currentVersion) {
        // Persist special keys from localStorage and add them back after localStorage.clear
        const cypressLogin = localStorage.getItem("cypress-login")
        const arrigoId = localStorage.getItem("arrigoId")
        const oauth = localStorage.getItem("oauth")
        const locale = localStorage.getItem("locale")
        const releaseFirstSeen = localStorage.getItem("RELEASE_FIRST_SEEN")
        const lastViewedRelease = localStorage.getItem("LAST_VIEWED_RELEASE")

        localStorage.clear()

        if (cypressLogin) {
          localStorage.setItem("cypress-login", cypressLogin)
        }

        if (arrigoId) {
          localStorage.setItem("arrigoId", arrigoId)
        }

        if (oauth) {
          localStorage.setItem("oauth", oauth)
        }

        if (locale) {
          localStorage.setItem("locale", locale)
        }

        if (releaseFirstSeen) {
          localStorage.setItem("RELEASE_FIRST_SEEN", releaseFirstSeen)
        }

        if (lastViewedRelease) {
          localStorage.setItem("LAST_VIEWED_RELEASE", lastViewedRelease)
        }

        localStorage.setItem("APP_VERSION", newVersion)
      }
    } catch (e) {
      console.error("Error verifying version.", e)
    }
  }

  // Provides the loadPackages fn to PackageLoader.
  // This is the pattern used so one Component can call a Core function
  wrapLoadPackages = (PackageLoader) => {
    const getProxy = this.getProxy
    const _loadPackages = this.loadPackages
    const components = this._components
    return class extends React.PureComponent {
      render() {
        return <PackageLoader {...this.props} loadPackages={_loadPackages} components={getProxy(components)} />
      }
    }
  }

  constructor(props) {
    super(props)
    this._isMounted = false

    const { preloadedModules } = props.appData || {}

    if (preloadedModules && preloadedModules.length > 0) {
      // Modules were pre-loaded before rendering — initialize synchronously so Core renders the full component tree on first paint.
      this._components["PackageLoader"] = this.wrapLoadPackages(PackageLoader)
      this.packagesLoaded = [...props.packages]
      localStorage.setItem("APP_LOADED_PACKAGES", [])
      const validModules = preloadedModules.filter(Boolean)
      for (let i = 0; i < validModules.length; i++) {
        this._readModuleSync(validModules[i])
      }
      this.composeComponents(Object.keys(this.originalComponents))
      Object.keys(this.Components).forEach((name) => (this._components[name] = this.Components[name][0]))
      this.verifyVersion(localStorage.getItem("APP_VERSION"))
    } else {
      // Fallback: async load (used if preloadedModules not provided).
      this.verifyVersion(localStorage.getItem("APP_VERSION")).then(() => {
        this.loadPackages(props.packages).then(() => {
          if (this._isMounted) {
            this.setState({ lastUpdate: Date.now() })
          }
        })
        this._components["PackageLoader"] = this.wrapLoadPackages(PackageLoader)
      })
    }

    /* Prevents gestures like pinch to zoom */
    document.addEventListener("gesturestart", function (e) {
      e.preventDefault()
    })
  }

  // Synchronous variant of readModule for use with pre-loaded modules.
  // Dependencies are not resolved here; callers are expected to have
  // pre-loaded all required packages before calling this method.
  _readModuleSync = (module) => {
    if (module["Managers"] !== undefined) {
      this.addManagers(module["Managers"])
    }
    if (module["Components"] !== undefined) {
      this.addComponents(module["Components"])
    }
    for (let i = 0; i < this.managers.length; i++) {
      const Manager = this.managers[i]
      if (typeof Manager.onModuleLoaded === "function") {
        Manager.onModuleLoaded(module)
      }
    }
  }

  componentDidMount() {
    this._isMounted = true
  }

  componentWillUnmount() {
    this._isMounted = false
  }
  // The Provider function is a pattern to wrap the whole App.
  // It provides a global feature, like the Store, the Theme and so on.
  Provider = (WrappedComponent) => {
    const Components = this._components

    return class ComponentWithComponents extends React.PureComponent {
      render() {
        return <WrappedComponent {...this.props} Components={Components} Loading={CoreLoading} />
      }
    }
  }

  _warnedComponents = new Set()

  getProxy = (components) => {
    const warnedComponents = this._warnedComponents
    const componentProxy = {
      get: (obj, prop) => {
        // typeof prop === "string" prevents crash on react-dev-tools
        // !prop.startsWith("$") prevents false positives for React internals like $$typeof
        if (prop && typeof prop === "string" && !prop.startsWith("$") && !obj[prop] && !warnedComponents.has(prop)) {
          warnedComponents.add(prop)
          console.log(
            `Component ${prop} is not yet loaded!
			Wrap the component in a
			<PackageLoader packages={ arrayOfPackageNames } />
				{(lazyLoad, components)=>{
					const { ${prop} } = components;
					<${prop} .../>
				}
			</PackageLoader>
			to use the component directly after loading.`
          )
        }
        return obj[prop]
      },
    }
    return new Proxy(components, componentProxy)
  }
  // The Composer is a pattern to add functionality to a Component.
  Composer = (WrappedComponent) => {
    const Components = this.getProxy(this._components)
    return class ComponentWithComponents extends React.PureComponent {
      render() {
        return <WrappedComponent {...this.props} Components={Components} />
      }
    }
  }

  loadPackages = (packages = []) => {
    return new Promise(async (resolve, reject) => {
      if (DEBUG === true) {
        console.warn("Asked to load: " + packages.join(","))
      }

      localStorage.setItem("APP_LOADED_PACKAGES", [])
      // Preventing importing the same package twice.
      const packagesNeeded = packages.filter((name) => this.packagesLoaded.indexOf(name) === -1)

      // If the Promise returns undefined, it means nothing was loaded.
      if (packagesNeeded.length === 0) {
        return resolve()
      }
      if (DEBUG === true) {
        console.warn("Loaded: " + this.packagesLoaded.join(","))
        console.warn("Loading: " + packagesNeeded.join(","))
      }
      this.packagesLoaded = [...this.packagesLoaded, ...packagesNeeded]
      const modules = await this.fetch(packages)
      for (let i = 0; i < modules.length; i++) {
        await this.readModule(modules[i])
      }
      // modules.map(module => this.readModule(module));
      // Name of all imported Components
      this.composeComponents(Object.keys(this.originalComponents))
      // Updated _components, please check the top of the Class.
      Object.keys(this.Components).map((name) => (this._components[name] = this.Components[name][0]))

      // Return the an callback to update the Core. Is the responsibility of
      // the caller to decide when this is needed.
      if (DEBUG === true) console.warn("Loaded: " + packagesNeeded.join(","))

      return resolve(this.forceCoreUpdate)
    })
  }

  composeComponents(listOfComponents) {
    for (let n = 0; n < listOfComponents.length; n++) {
      const name = listOfComponents[n]
      // Please see the explanation at the top of the Class.
      this.Components[name][0] = this.originalComponents[name]
      for (let i = 0; i < this.managers.length; i++) {
        const Manager = this.managers[i]
        // If the Manager doesn't have a Composer function, skipped
        if (Manager.Composer === undefined) continue
        this.Components[name][0] = Manager.Composer(this.Components[name][0], this.Components[name][1])
      }

      // If skipCompose is true, the component doesn't need the Components prop.
      if (this.Components[name][1].skipCompose === true) continue
      this.Components[name][0] = this.Composer(this.Components[name][0], this.Components[name][1])
    }
  }

  readModule = async (module) => {
    if (module["Dependencies"] !== undefined) {
      await this.loadPackages(module["Dependencies"])
    }
    if (module["Managers"] !== undefined) {
      this.addManagers(module["Managers"])
    }
    if (module["Components"] !== undefined) {
      this.addComponents(module["Components"])
    }
    for (let i = 0; i < this.managers.length; i++) {
      const Manager = this.managers[i]
      if (typeof Manager.onModuleLoaded === "function") {
        Manager.onModuleLoaded(module)
      }
    }
  }

  addComponents(Components) {
    Object.keys(Components).map((name) => {
      const Component = Components[name]
      if (Array.isArray(Component)) {
        this.Components[name] = [Component[0], Component[1]]
        this.originalComponents[name] = Component[0]
      } else {
        this.Components[name] = [Component, {}]
        this.originalComponents[name] = Component
      }
      return name
    })
  }

  addManagers(Managers) {
    // Build the Provider and Composer chains in a deterministic order regardless
    // of module iteration order. Lower PROVIDER_ORDER = outermost React wrapper.
    // RouteManager must be outermost so the router is available to all providers.
    // StoreManager must wrap TranslationsManager and GraphQLManager since those
    // receive the store as a prop.
    const PROVIDER_ORDER = {
      RouteManager: 0,
      StoreManager: 1,
      ThemeManager: 2,
      TranslationsManager: 3,
      HelpersManager: 4,
      GraphQLManager: 5, // export name
      GraphQlManager: 5, // constructor.name
      DeviceManager: 6,
      AutobahnManager: 7,
      AdminConfigManager: 8,
    }

    // Lower COMPOSER_ORDER = innermost HOC (applied first).
    // RouteManager Composer must be outermost so all inner Composers receive
    // the match/location props it injects.
    const COMPOSER_ORDER = {
      StoreManager: 0,
      ThemeManager: 1,
      TranslationsManager: 2,
      HelpersManager: 3,
      GraphQLManager: 4,
      GraphQlManager: 4,
      DeviceManager: 5,
      AutobahnManager: 6,
      AdminConfigManager: 7,
      RouteManager: 8, // must be last = outermost Composer
    }

    const entries = Object.keys(Managers).map((name) => {
      const Manager = Managers[name]
      this.managers.unshift(Manager)
      return { name, Manager }
    })

    // Sort managers so Composers are applied in the correct order.
    // composeComponents iterates managers[0]..managers[n], so the last entry
    // becomes the outermost HOC wrapper.
    this.managers.sort((a, b) => {
      const pa = COMPOSER_ORDER[a.constructor?.name] ?? 99
      const pb = COMPOSER_ORDER[b.constructor?.name] ?? 99
      return pa - pb
    })

    // mergeFunctions(a, b)(x) = a(b(x)) — first in forEach = outermost in the tree.
    entries
      .filter(({ Manager }) => typeof Manager.Provider === "function")
      .sort((a, b) => {
        const pa = PROVIDER_ORDER[a.name] ?? 99
        const pb = PROVIDER_ORDER[b.name] ?? 99
        return pa - pb
      })
      .forEach(({ Manager }) => {
        this.Provider = mergeFunctions(this.Provider, Manager.Provider)
      })
  }

  fetch = (list) => Promise.all(list.map((module) => loadPackage(module)))

  forceCoreUpdate = (cb) => {
    if (DEBUG === true) console.log("Forced update on Core")
    localStorage.setItem("APP_LOADED_PACKAGES", this.packagesLoaded)
    return this.setState({ lastUpdate: Date.now() }, () => typeof cb === "function" && cb())
  }

  render() {
    if (Object.keys(this.Components).length === 0) {
      return <CoreLoading centered />
    }
    // Build MyProvider exactly once and never rebuild it.
    // - Rebuilding creates new HOC class types, causing React to unmount/remount the
    //   entire provider tree (StoreProvider, ThemeProvider, etc.) on every re-render.
    //   This resets StoreProvider's async state and breaks store prop propagation.
    // - Dynamic package loading via PackageLoader mutates this._components in place,
    //   so CoreProvider already picks up new components without needing a new class.
    // - New managers added by dynamic packages only add Composer wrappers (they cannot
    //   retroactively add Provider wrappers to an already-mounted tree anyway).
    if (!this._MyProvider) {
      this._MyProvider = this.Provider(({ Components: { App } }) => (
        <App network={this.props.network} account={this.props.account} />
      ))
    }
    const MyProvider = this._MyProvider

    return (
      <>
        <CoreLoading />
        <MyProvider appData={this.props.appData} />
      </>
    )
  }
}
export default Core
